import { cva, type VariantProps } from 'class-variance-authority'
import type { LucideIcon } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { Icon } from './Icon'

/**
 * Fab — Floating Action Button. Fixed position, bottom-right.
 * Primary action that stays visible at any scroll position.
 */
export const fabVariants = cva(
  [
    'fixed z-40 inline-flex items-center justify-center gap-2',
    'rounded-pill shadow-lg font-medium',
    'transition-all duration-200',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'hover:shadow-xl hover:scale-105',
    'active:scale-95',
    'disabled:pointer-events-none disabled:opacity-50',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground hover:bg-[var(--color-primary-hover)]',
        destructive: 'bg-destructive text-destructive-foreground',
      },
      size: {
        md: 'h-12 min-w-12 px-4 text-body-sm [&_svg]:size-[18px]',
        lg: 'h-14 min-w-14 px-5 text-body-md [&_svg]:size-[20px]',
      },
      position: {
        'bottom-right': 'bottom-6 right-6',
        'bottom-left': 'bottom-6 left-6',
        'bottom-center': 'bottom-6 left-1/2 -translate-x-1/2',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md', position: 'bottom-right' },
  },
)

export interface FabProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof fabVariants> {
  icon?: LucideIcon
  /** When true, only shows the icon (no label) */
  iconOnly?: boolean
}

export const Fab = React.forwardRef<HTMLButtonElement, FabProps>(
  ({ className, variant, size, position, icon, iconOnly, children, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn(fabVariants({ variant, size, position }), iconOnly && 'px-0', className)}
      {...props}
    >
      {icon && <Icon icon={icon} />}
      {!iconOnly && children}
    </button>
  ),
)
Fab.displayName = 'Fab'
